import { useCallback, useEffect } from 'react'
import { atom, useRecoilState } from 'recoil'
import { CommonObject, IdNamePair } from '@/types/common'
import { fetchAllRegions, getGrades, getSemesters, getAdministrativeRegions } from '@/services/dict'
import { RegionItem } from '@/types/dict'

export const gradesAtom = atom<IdNamePair[]>({
  key: 'dict-grades-atom',
  default: [],
})

export const gradesMapAtom = atom<Record<string | number, string>>({
  key: 'dict-grades-map-atom',
  default: {},
})

export const semesterAtom = atom<IdNamePair[]>({
  key: 'dict-semester-atom',
  default: [],
})

export const semesterMapAtom = atom<Record<string | number, string>>({
  key: 'dict-semester-map-atom',
  default: {},
})

export const totalRegion = atom<RegionItem[]>({
  key: 'total-region',
  default: [],
})

export const totalRegionMap = atom<CommonObject<RegionItem>>({
  key: 'total-region-map',
  default: {},
})

export const totalAdminRegion = atom<RegionItem[]>({
  key: 'total-admin-region',
  default: [],
})

export const totalAdminRegionMap = atom<CommonObject<RegionItem>>({
  key: 'total-admin-region-map',
  default: {},
})

export function useRegion() {
  const [, setTotalRegion] = useRecoilState(totalRegion)
  const [, setTotalRegionMap] = useRecoilState(totalRegionMap)

  useEffect(() => {
    const getData = async () => {
      const { result } = await fetchAllRegions()
      setTotalRegion(result.children)
      const map: CommonObject<RegionItem> = {}
      function traverse(item: RegionItem) {
        map[item.regionId] = item
        if (item.children && item.children.length) {
          for (const child of item.children) {
            traverse(child)
          }
        }
      }
      traverse(result)
      setTotalRegionMap(map)
    }
    getData()
  }, [setTotalRegion, setTotalRegionMap])
}

export function useAdminRegion() {
  const [, setTotalAdminRegion] = useRecoilState(totalAdminRegion)
  const [, setTotalAdminRegionMap] = useRecoilState(totalAdminRegionMap)

  useEffect(() => {
    const getData = async () => {
      const { result } = await getAdministrativeRegions()
      setTotalAdminRegion(result.children)
      const map: CommonObject<RegionItem> = {}
      function traverse(item: RegionItem) {
        map[item.regionId] = item
        if (item.children && item.children.length) {
          for (const child of item.children) {
            traverse(child)
          }
        }
      }
      traverse(result)
      setTotalAdminRegionMap(map)
    }
    getData()
  }, [setTotalAdminRegion, setTotalAdminRegionMap])
}

export function useGrades() {
  const [, setGrades] = useRecoilState(gradesAtom)
  const [, setGradesMap] = useRecoilState(gradesMapAtom)
  const getData = useCallback(async () => {
    try {
      const { code, message: msg, result } = await getGrades()
      if (code === '0') {
        setGrades(result)
        const map: Record<string | number, string> = {}
        for (const item of result) {
          map[item.id] = item.name
        }
        setGradesMap(map)
      } else {
        console.warn(msg)
        setGrades([])
        setGradesMap({})
      }
    } catch (e) {
      console.warn(e)
      setGrades([])
      setGradesMap({})
    }
  }, [setGrades, setGradesMap])

  useEffect(() => {
    getData()
  }, [getData])
}

export function useSemesters() {
  const [, setSemesters] = useRecoilState(semesterAtom)
  const [, setSemestersMap] = useRecoilState(semesterMapAtom)
  const getData = useCallback(async () => {
    try {
      const { code, message: msg, result } = await getSemesters()
      if (code === '0') {
        setSemesters(result)
        const map: Record<string | number, string> = {}
        for (const item of result) {
          map[item.id] = item.name
        }
        setSemestersMap(map)
      } else {
        console.warn(msg)
        setSemesters([])
        setSemestersMap({})
      }
    } catch (e) {
      console.warn(e)
      setSemesters([])
      setSemestersMap({})
    }
  }, [setSemesters, setSemestersMap])

  useEffect(() => {
    getData()
  }, [getData])
}
